import type {
  ReportTocGroupType as ReportTocGroup,
  ReportTocItemType as ReportTocItem,
  ReportTocSectionType as ReportTocSection,
  ReportTocCollectionEntryType as ReportTocCollectionEntry,
  ReportTocCollectionType as ReportTocCollection,
  TemplateGroup,
  TemplateSection,
} from '@/data/schemas';
import { slugifyItalian } from '@/data/idGenerator';

type TocMeta = TemplateGroup;

interface TocGroupBuilder {
  meta: TocMeta;
  items: ReportTocItem[];
  itemIndexByTemplateId: Map<string, number>;
}

interface TocSectionInput {
  navigationGroup: NonNullable<TemplateSection['navigationGroup']>;
  sectionId: string;
  templateSectionId: string;
  label: string;
  unitIndex?: number;
  unitLabel?: string;
}

export function createTocBuilder(initialGroups?: TemplateGroup[]) {
  const groupBuilders = new Map<string, TocGroupBuilder>();
  const ORDER_SCALE = 100;
  const UNIT_GROUP_ID = 'centrali';

  const seedGroup = (groupMeta: TemplateGroup): TocGroupBuilder => {
    const existing = groupBuilders.get(groupMeta.id);
    if (existing) {
      existing.meta = { ...existing.meta, ...groupMeta };
      return existing;
    }
    const builder: TocGroupBuilder = {
      meta: { ...groupMeta },
      items: [],
      itemIndexByTemplateId: new Map(),
    };
    groupBuilders.set(groupMeta.id, builder);
    return builder;
  };

  initialGroups?.forEach((group) => {
    seedGroup({
      ...group,
      order: group.order * ORDER_SCALE,
    });
  });

  const formatUnitLabel = (unitLabel: string, unitIndex?: number): string => {
    const fallback = unitIndex !== undefined ? `Impianto ${unitIndex + 1}` : 'Impianto';
    const normalized = unitLabel.trim();
    if (!normalized) return fallback;
    const lower = normalized.toLowerCase();
    if (lower.startsWith('impianto')) return normalized;
    const stripped = normalized.replace(/^centrale\s*/i, '').trim();
    if (!stripped) return fallback;
    return stripped.toLowerCase().startsWith('impianto') ? stripped : `Impianto ${stripped}`;
  };

  const normalizeUnitLabel = (rawLabel?: string): { label: string; hasLabel: boolean } => {
    const trimmed = rawLabel?.trim() ?? '';
    if (!trimmed) {
      return { label: '', hasLabel: false };
    }
    if (trimmed.includes('{{') || trimmed.includes('}}')) {
      return { label: '', hasLabel: false };
    }
    return { label: trimmed, hasLabel: true };
  };

  const resolveGroupMeta = (
    groupMeta: NonNullable<TemplateSection['navigationGroup']>,
    unitIndex?: number,
    unitLabel?: string
  ): TemplateGroup => {
    const baseOrder = groupMeta.order * ORDER_SCALE;
    if (groupMeta.id === UNIT_GROUP_ID && (unitLabel || unitIndex !== undefined)) {
      const normalized = normalizeUnitLabel(unitLabel);
      const label = formatUnitLabel(normalized.label, unitIndex);
      const idSuffix = normalized.hasLabel
        ? slugifyItalian(normalized.label) || 'unit'
        : String(unitIndex ?? 0);
      return {
        id: `${groupMeta.id}-${idSuffix}`,
        label,
        order: baseOrder + (unitIndex ?? 0),
        icon: 'dns',
      };
    }
    return {
      id: groupMeta.id,
      label: groupMeta.label,
      order: baseOrder,
      icon: groupMeta.icon,
    };
  };

  const ensureGroup = (groupMeta: TemplateGroup): TocGroupBuilder => {
    const group = seedGroup(groupMeta);
    group.meta = { ...group.meta, ...groupMeta };
    return group;
  };

  const addSection = ({
    navigationGroup,
    sectionId,
    templateSectionId,
    label,
    unitIndex,
    unitLabel,
  }: TocSectionInput) => {
    const resolvedGroup = resolveGroupMeta(navigationGroup, unitIndex, unitLabel);
    const group = ensureGroup(resolvedGroup);
    if (unitIndex !== undefined) {
      const normalized = normalizeUnitLabel(unitLabel);
      const entry: ReportTocCollectionEntry = {
        sectionId,
        templateSectionId,
        label,
        unitIndex,
        unitLabel: normalized.label || undefined,
      };

      const existingIndex = group.itemIndexByTemplateId.get(templateSectionId);
      if (existingIndex === undefined) {
        const collection: ReportTocCollection = {
          type: 'collection',
          templateSectionId,
          label,
          entries: [entry],
        };
        group.items.push(collection);
        group.itemIndexByTemplateId.set(templateSectionId, group.items.length - 1);
        return;
      }

      const existingItem = group.items[existingIndex];
      if (existingItem.type === 'collection') {
        existingItem.entries.push(entry);
        return;
      }

      const legacyEntry: ReportTocCollectionEntry = {
        sectionId: existingItem.sectionId,
        templateSectionId: existingItem.templateSectionId,
        label: existingItem.label,
        unitIndex: Math.max(unitIndex - 1, 0),
        unitLabel,
      };
      group.items[existingIndex] = {
        type: 'collection',
        templateSectionId,
        label: existingItem.label,
        entries: [legacyEntry, entry],
      };
      return;
    }

    const tocSection: ReportTocSection = {
      type: 'section',
      sectionId,
      templateSectionId,
      label,
    };
    group.items.push(tocSection);
    group.itemIndexByTemplateId.set(templateSectionId, group.items.length - 1);
  };

  const build = (): ReportTocGroup[] =>
    Array.from(groupBuilders.values())
      .filter(({ items }) => items.length > 0)
      .map(({ meta, items }) => ({
        id: meta.id,
        label: meta.label,
        icon: meta.icon ?? 'description',
        order: meta.order,
        items,
      }))
      .sort((a, b) => a.order - b.order);

  return { addSection, build };
}
